import {
    classImplementsInterfaces,
    ColorTransform,
    GradientType,
    Matrix,
    SpreadMethod
} from "@goodgamestudios/createjs-utilities";
import {EaselPlugin, Elastic, Linear, Power4, TweenLite, TweenMax} from "gsap";
import {MathUtils} from "../../../util/MathUtils";
import Shape = createjs.Shape;
import Container = createjs.Container;
import Event = createjs.Event;
import DisplayObject = createjs.DisplayObject;

export interface IRGB{
    r: number;
    g: number;
    b: number;
}

export class TurnTimerComponent extends Container {

    /** Public callback function when the timer has finished. Will pass a parameter: 1 = timer complete done, 0.5 = timer half done. **/
    public onTurnTimerCallbackTrigger: Function;

    private static COLOR_START_HEX: number = 0x6EB614; // green
    private static COLOR_DARK_GREEN_HEX1: number = 0x53A01D;
    private static COLOR_DARK_GREEN_HEX2: number = 0x417D17;
    private static COLOR_LIGHT_GREEN_HEX: number = 0x69CC26;
    private static COLOR_WHITE_HEX: number = 0xffffff;
    private static COLOR_BLACK_HEX: number = 0x000000;
    private static COLOR_GRAY_HEX1: number = 0x666666;
    private static COLOR_GRAY_HEX2: number = 0x111111;

    private static COLOR_START: IRGB = {r: 110, g: 182, b: 20}; // #6eb614 - green
    private static COLOR_MIDDLE: IRGB = {r: 188, g: 182, b: 38}; // #bcb626 - yellow-green
    private static COLOR_END: IRGB = {r: 153, g: 0, b: 0}; // #990000 - dark red
    private static COLOR_PULSE_OUTER: IRGB = {r: 105, g: 204, b: 38}; // #69cc26 - green
    private static COLOR_PULSE_INNER: IRGB = {r: 83, g: 160, b: 29}; // #53a01d - green

    private _rotProxyValueObject: any = {};

    // values
    private _timerDuration: number = NaN;
    private _startWedgeAngle: number = 360;
    private _endWedgeAngle: number = 0;
    private _time25Percent: number = NaN;
    private _time50Percent: number = NaN;
    private _time75Percent: number = NaN;
    private _pulsateTime: number = NaN;
    private _pulsateAmount: number = NaN;

    // views
    private _bgGradientCircle: Container;
    private _lightGreenCircle: Shape;
    private _whiteCircle: Shape;
    private _blackKnob: Shape;
    private _wedgeMask: Shape;
    private _transformCircle: Shape;
    private _gradientKnob: Shape;

    // flags
    private _isRunning: boolean = false;
    private _fiftyPercentDone: boolean = false;
    private _seventyFivePercentDone: boolean = false;

    // tweens
    private _gt_show: TweenLite;
    private _gt_hide: TweenLite;
    private _gt_openWedge: TweenMax;

    /** gradient tween 1: from start to middle color **/
    private _gt_bgColorTransform_startToMiddle: TweenMax;

    /** gradient tween 2: from middle to end color **/
    private _gt_bgColorTransform_middleToEnd: TweenMax;

    /** Let the circle color pulsate **/
    private _gt_colorPulsate: TweenMax;

    private _gt_colorInnerPulsate: TweenMax;

    /** Let the circle scale pulsate **/
    private _gt_scalelPulsate: TweenMax;

    constructor() {
        super();

        this.addEventListener(Event.ADDED_TO_STAGE, this.bindFunction(this.onAddedToStage));

        this.initSettings();
    }

    private initSettings(): void {
        // the timer view will pulsate three times at 75% of the timer duration
        this.pulsateAmount = 3;
    }

    protected onAddedToStage(event: Event): void {
        this.removeEventListener(Event.ADDED_TO_STAGE, this.bindFunction(this.onAddedToStage));

        this.drawComponent();
        this.scaleX = this.scaleY = 0;
        this.resetMask();
    }

    /**
     * Starts the timer.
     * @param timeInSeconds, Number: Duration in seconds
     */
    public startTimer(timeInSeconds: number): void {
        if (isNaN(timeInSeconds) || timeInSeconds < 0) {
            console.log("TurnTimerComponent, startTimer() -> invalid time: " + timeInSeconds);
            return;
        }

        // store current time values and precalculate often needed values
        this._timerDuration = timeInSeconds;
        this._time25Percent = timeInSeconds * .25;
        this._time50Percent = timeInSeconds * .5;
        this._time75Percent = timeInSeconds * .75;
        this._pulsateTime = this._time25Percent * (1 / 6);

        // reset
        this._rotProxyValueObject = {};
        this._isRunning = true;
        this._fiftyPercentDone = false;
        this._seventyFivePercentDone = false;
        this.kill();

        this.resetTimerComponents();
        this.drawComponent();

        // reset view
        this.resetMask();

        // start animation
        this.startWedgeAnimation();
        this.show();
    }

    private show(): void {
        if (this._gt_hide) {
            this._gt_hide.paused(true);
            this._gt_hide = null;
        }
        this._gt_show = new TweenLite(this, 0.8, {scaleX: 1, scaleY: 1, ease: Elastic.easeOut});
        this._gt_show.play();
    }

    private playHurryUpAnimation(): void {
        if (!this._isRunning) {
            return;
        }

        this._lightGreenCircle.graphics.clear();
        this.drawCircleInShape(this._lightGreenCircle, TurnTimerComponent.COLOR_DARK_GREEN_HEX1, 11.5);

        let startInnerColor: IRGB = {r: TurnTimerComponent.COLOR_PULSE_INNER.r, g: TurnTimerComponent.COLOR_PULSE_INNER.g, b: TurnTimerComponent.COLOR_PULSE_INNER.b};
        this._gt_colorInnerPulsate = new TweenMax(startInnerColor, this._pulsateTime, {
            r: TurnTimerComponent.COLOR_END.r,
            g: TurnTimerComponent.COLOR_END.g,
            b: TurnTimerComponent.COLOR_END.b,
            repeat: this.pulsateAmount,
            yoyo: true,
            onUpdate: this.bindFunction(this.onUpdate),
            onUpdateParams: ["{self}", this._lightGreenCircle]
        });
        this._gt_colorInnerPulsate.play();

        // redraw a separate circle for the pulsate, because the one with gradient cannot be tweened
        this._bgGradientCircle.graphics.clear();
        this.drawCircleInShape(this._bgGradientCircle, TurnTimerComponent.COLOR_LIGHT_GREEN_HEX, 14);

        let startOuterColor: IRGB = {r: TurnTimerComponent.COLOR_PULSE_OUTER.r, g: TurnTimerComponent.COLOR_PULSE_OUTER.g, b: TurnTimerComponent.COLOR_PULSE_OUTER.b};
        this._gt_colorPulsate = new TweenMax(startOuterColor, this._pulsateTime, {
            r: TurnTimerComponent.COLOR_END.r,
            g: TurnTimerComponent.COLOR_END.g,
            b: TurnTimerComponent.COLOR_END.b,
            repeat: this.pulsateAmount,
            yoyo: true,
            onUpdate: this.bindFunction(this.onUpdate),
            onUpdateParams: ["{self}", this._bgGradientCircle]
        });
        this._gt_colorPulsate.play();

        this._gt_scalelPulsate = new TweenMax(this, this._pulsateTime, {
            scaleX: 1.3,
            scaleY: 1.3,
            repeat: this.pulsateAmount,
            yoyo: true
        });
        this._gt_scalelPulsate.play();
    }

    private onUpdate(tween: TweenMax, tweenTarget: DisplayObject): void {
        let tweenedColor = tween.target;

        tweenTarget["command"].style = createjs.Graphics.getRGB(Math.floor(tweenedColor.r), Math.floor(tweenedColor.g), Math.floor(tweenedColor.b));
    }

    private drawComponent(): void {
        // bg
        this._bgGradientCircle = new Container();
        this.drawGradientCircleInShape(this._bgGradientCircle, [TurnTimerComponent.COLOR_LIGHT_GREEN_HEX, TurnTimerComponent.COLOR_DARK_GREEN_HEX2], 14);
        this.addChild(this._bgGradientCircle);

        // wedge mask
        this._wedgeMask = new Shape();
        this._wedgeMask.scaleX = -1;
        this._wedgeMask.rotation = 90;
        this.addChild(this._wedgeMask);

        // light green frame
        this._lightGreenCircle = new Shape();
        this.drawGradientCircleInShape(this._lightGreenCircle, [TurnTimerComponent.COLOR_DARK_GREEN_HEX1, TurnTimerComponent.COLOR_LIGHT_GREEN_HEX], 11.5);
        this._bgGradientCircle.addChild(this._lightGreenCircle);

        // gradient layer
        this._transformCircle = new Shape();
        this.drawCircleInShape(this._transformCircle, TurnTimerComponent.COLOR_START_HEX, 11);
        this.addChild(this._transformCircle);

        // white
        this._whiteCircle = new Shape();
        this.drawGradientCircleInShape(this._whiteCircle, [TurnTimerComponent.COLOR_WHITE_HEX, TurnTimerComponent.COLOR_WHITE_HEX], 11);
        this.addChild(this._whiteCircle);

        // gradient knob
        this._gradientKnob = new Shape();
        this.drawGradientCircleInShape(this._gradientKnob, [TurnTimerComponent.COLOR_GRAY_HEX1, TurnTimerComponent.COLOR_GRAY_HEX2], 1.8);
        this.addChild(this._gradientKnob);

        // black knob
        this._blackKnob = new Shape();
        this.drawGradientCircleInShape(this._blackKnob, [TurnTimerComponent.COLOR_BLACK_HEX, TurnTimerComponent.COLOR_BLACK_HEX], 1.5);
        this.addChild(this._blackKnob);
    }

    private resetMask(): void {
        if (this._transformCircle) {
            this._transformCircle.transform.colorTransform = new ColorTransform(TurnTimerComponent.COLOR_START.r, TurnTimerComponent.COLOR_START.g, TurnTimerComponent.COLOR_START.b);
        }
        this._whiteCircle.mask = null;
        this._rotProxyValueObject.angle = this._startWedgeAngle;
        this.drawWedge(this._wedgeMask, 0, 0, 11.5, 360, 360);
    }

    private drawCircleInShape(target: DisplayObject, color: number, radius: number): void {
        target["command"] = target.graphics.beginFill(color).command;
        target.graphics.drawCircle(0, 0, radius);
        target.graphics.endFill();
    }

    private drawGradientCircleInShape(target: DisplayObject, gradientColors: any[], radius: number): void {
        var fillType: string = GradientType.LINEAR;
        var colors: any[] = gradientColors;
        var alphas: any[] = [1, 1];
        var ratios: any[] = [0, 255];
        var matr: Matrix = new Matrix();
        matr.createGradientBox(radius, radius, MathUtils.degreesToRadians(90), 0, 0);
        var spreadMethod: string = SpreadMethod.PAD;

        target.graphics.beginGradientFill(fillType, colors, alphas, ratios, matr, spreadMethod);
        target.graphics.drawCircle(0, 0, radius);
        target.graphics.endFill();
    }

    private startWedgeAnimation(): void {
        this._whiteCircle.mask = this._wedgeMask;

        let startColor: IRGB = {
            r: TurnTimerComponent.COLOR_START.r,
            g: TurnTimerComponent.COLOR_START.g,
            b: TurnTimerComponent.COLOR_START.b
        };
        this._gt_bgColorTransform_startToMiddle = new TweenMax(startColor, this._time50Percent, {
            r: TurnTimerComponent.COLOR_MIDDLE.r,
            g: TurnTimerComponent.COLOR_MIDDLE.g,
            b: TurnTimerComponent.COLOR_MIDDLE.b,
            onUpdate: this.bindFunction(this.onUpdate),
            onUpdateParams: ["{self}", this._transformCircle],
            ease: Linear.easeNone,
            onComplete: this.bindFunction(this.onGradientTransformHalfComplete),
            onCompleteParams: ["{self}", this._transformCircle]
        });
        this._gt_bgColorTransform_startToMiddle.play();

        // increasing the wedge angle
        this._gt_openWedge = new TweenMax(this._rotProxyValueObject, this._timerDuration, {
            angle: this._endWedgeAngle,
            ease: Linear.easeNone,
            onUpdate: this.bindFunction(this.onChangeAngle),
            onUpdateParams: ["{self}"],
            onComplete: this.bindFunction(this.onTurnTimerComplete)
        });
        this._gt_openWedge.play();
    }

    private onGradientTransformHalfComplete(tween: TweenMax, tweenTarget: DisplayObject): void {
        this._gt_bgColorTransform_middleToEnd = new TweenMax(tween.target, this._time50Percent, {
            r: TurnTimerComponent.COLOR_END.r,
            g: TurnTimerComponent.COLOR_END.g,
            b: TurnTimerComponent.COLOR_END.b,
            ease: Linear.easeNone,
            onUpdate: this.bindFunction(this.onUpdate),
            onUpdateParams: ["{self}", tweenTarget]
        });
        this._gt_bgColorTransform_middleToEnd.play();
    }

    private onTurnTimerComplete(gt: TweenLite): void {

        this.handleTimerComplete(1);
    }

    private handleTimerComplete(progress: number = 0): void {
        // 1 = timer has finished
        if (progress > 0) {
            this.onTurnTimerCallbackTrigger(progress);
        }

        // hide view
        this.hide();
    }

    private resetTimerComponents(): void {
        // bg
        this.removeChild(this._bgGradientCircle);
        this._bgGradientCircle = null;

        // wedge mask
        this.removeChild(this._wedgeMask);
        this._wedgeMask = null;

        // light green frame
        this.removeChild(this._lightGreenCircle);
        this._lightGreenCircle = null;

        // gradient layer
        this.removeChild(this._transformCircle);
        this._transformCircle = null;

        // white
        this.removeChild(this._whiteCircle);
        this._whiteCircle = null;

        // gradient knob
        this.removeChild(this._gradientKnob);
        this._gradientKnob = null;

        // black knob
        this.removeChild(this._blackKnob);
        this._blackKnob = null;
    }

    private kill(): void {
        if (this._gt_show) {
            this._gt_show.kill();
            this._gt_show = null;
        }
        if (this._gt_hide) {
            this._gt_hide.kill();
            this._gt_hide = null;
        }

        if (this._gt_bgColorTransform_startToMiddle) {
            this._gt_bgColorTransform_startToMiddle.kill();
            this._gt_bgColorTransform_startToMiddle = null;
        }
        if (this._gt_bgColorTransform_middleToEnd) {
            this._gt_bgColorTransform_middleToEnd.kill();
            this._gt_bgColorTransform_middleToEnd = null;
        }
        if (this._gt_openWedge) {
            this._gt_openWedge.kill();
            this._gt_openWedge = null;
        }
        if (this._gt_colorPulsate) {
            this._gt_colorPulsate.kill();
            this._gt_colorPulsate = null;
        }
        if (this._gt_colorInnerPulsate) {
            this._gt_colorInnerPulsate.kill();
            this._gt_colorInnerPulsate = null;
        }
        if (this._gt_scalelPulsate) {
            this._gt_scalelPulsate.kill();
            this._gt_scalelPulsate = null;
        }

        this.removeEventListener(Event.ADDED_TO_STAGE, this.bindFunction(this.onAddedToStage));
    }

    /**
     * Stops all animations
     */
    public stopTimer(): void {
        this.stopTweens();

        // return the current progress
        var progress: number = (this._gt_openWedge) ? this.getProgressForRanges(this._gt_openWedge.progress(), 0, 1, 0, 20) : 0;
        this.handleTimerComplete(progress);
    }

    /**
     * Pauses all animations
     */
    public pauseTimer(): void {
        this.stopTweens();
    }

    private stopTweens(): void {
        this.kill();

        // calls the onComplete function 'onTurnTimerComplete()' of _gt_openWedge implicitely
        if (this._gt_bgColorTransform_startToMiddle) {
            this._gt_bgColorTransform_startToMiddle.paused(true);
        }
        if (this._gt_openWedge) {
            this._gt_openWedge.paused(true);
        }
    }

    private hide(): void {
        if (this._gt_show) {
            this._gt_show.paused(true);
            this._gt_show = null;
        }

        this._gt_hide = new TweenLite(this, .3, {scaleX: 0, scaleY: 0, ease: Power4.easeOut});
        this._gt_hide.onComplete = this.bindFunction(this.onHideComplete);
        this._gt_hide.play();
    }

    private onHideComplete(gt: TweenLite): void {
        this._isRunning = false;
    }

    /**
     * Stops all animations, but won't either hide the timer view or trigger the callback function.
     * If you wan't to dispose the timer call the stopTimer() method instead.
     */
    public stopAll(): void {
        this.stopTweens();
        this.scaleX = this.scaleY = 0;
    }

    private onChangeAngle(gt: TweenMax): void {

        // each player has 20 seconds to make an action
        let curentProgressInSeconds = this.getProgressForRanges(gt.progress(), 0, 1, 0, 20);

        // handle 50% done
        if (curentProgressInSeconds > this._time50Percent && !this._fiftyPercentDone) {
            this._fiftyPercentDone = true;

            // trigger callback
            this.onTurnTimerCallbackTrigger(0.5);
        }

        // handle 75% done
        if (curentProgressInSeconds > this._time75Percent && !this._seventyFivePercentDone) {
            this._seventyFivePercentDone = true;

            // start hurry up animation
            this.playHurryUpAnimation();
        }

        this.animateWedge();
    }

    /**
     * Converts a value from one range to another
     * @param currentProgress - current progress of the tween (it's a value between 0 and 1).
     * We want to convert it to a value range between secondsStart and secondsEnd
     * @param progressStart
     * @param progressEnd
     * @param secondsStart
     * @param secondsEnd
     * @returns {number}
     */
    private getProgressForRanges(currentProgress, progressStart, progressEnd, secondsStart, secondsEnd): number {
        return (currentProgress - progressStart) * ((secondsEnd - secondsStart) / (progressEnd - progressStart)) + secondsStart;
    }

    protected animateWedge(): void {
        this.drawWedge(this._wedgeMask, 0, 0, 14, this._startWedgeAngle, this._rotProxyValueObject.angle);
    }

    public drawWedge(target: Shape, x: number, y: number, radius: number, bA: number, eA: number): void {
        var degToRad: number = Math.PI / 180;

        if (eA < bA) eA += 360;

        var r: number = radius;
        var n: number = Math.ceil((eA - bA) / 45);
        var theta: number = ((eA - bA) / n) * degToRad;
        var cr: number = radius / Math.cos(theta / 2);
        var angle: number = bA * degToRad;
        var cangle: number = angle - theta / 2;

        target.graphics.clear();
        target.graphics.beginFill(TurnTimerComponent.COLOR_START_HEX, 1);
        target.graphics.moveTo(x, y);
        target.graphics.lineTo(x + r * Math.cos(angle), y + r * Math.sin(angle));

        var i: number = 0;
        for (i = 0; i < n; ++i) {
            angle += theta;
            cangle += theta;
            var endX: number = r * Math.cos(angle);
            var endY: number = r * Math.sin(angle);
            var cX: number = cr * Math.cos(cangle);
            var cY: number = cr * Math.sin(cangle);
            target.graphics.curveTo(x + cX, y + cY, x + endX, y + endY);
        }

        target.graphics.lineTo(x, y);
    }

    public get pulsateAmount(): number {
        return this._pulsateAmount;
    }

    // doubles the given values, due the to tweens are in reflect mode
    public set pulsateAmount(value: number) {
        this._pulsateAmount = value * 2;
    }
}

classImplementsInterfaces(TurnTimerComponent, "Container");
